<template>
  <div>
    <h3>{{ $t("tag.form.create.title", { resource }) }}</h3>
    <form v-if="categories && categories.length && !loading">
      <label class="text-gray">
        {{ this.$i18n.t("sources.category.title") }}
      </label>
      <input-select
        v-model="categorySelectedId"
        :searchable="true"
        :allow-empty="false"
        :options="categoriesToList"
        :disabled="disabledSeletCategory"
        :placeholder="$i18n.t('sources.category.select')"
        option-label="name"
        option-value="id"
      ></input-select>
    </form>
    <load-content :loading="!isReady || loading">
      <form-renderer
        id="add-tag-form"
        :key="`add-tag-form-${categorySelectedId}-${keyForm}`"
        :model.sync="modelTag"
        :errors="errors"
        :fields.sync="formFields"
        :fieldsAdditions="outletsFieldsAdditions"
        :submitButtonLabel="submitButtonLabel"
        :useDebounce="false"
        @updateModel="(value, field) => updateModel(value, field)"
        @onSave="save">
        <template #importexport>
          <div class="import-outlet-seciton">
            <div><span v-if="missingOutletIds.length" class="error">{{$i18n.t('form.missing_ids')}}{{missingOutletIds.join(', ')}}</span></div>
            <div class="btn-group">
              <button class="btn btn-default btn-xxs" type="button" @click="toggleImportOutletsModal(true)">{{$i18n.t('form.multiselect.import')}}</button>
              <button v-if="tagToEdit" class="btn btn-default btn-xxs" type="button" @click="outletExports">{{$i18n.t('form.multiselect.export')}}</button>
            </div>
          </div>
        </template>
      </form-renderer>
    </load-content>
    <FormModal 
      v-if="showImportOutletsModal" 
      id="import-outlets-form"
      title="form.import_outlets"
      icon="fas fa-download text-sm"
      :showSaveAs="false"
      :showDelete="false"
      :fields="['file_outlets_import']"
      @closeModal="() => toggleImportOutletsModal(false)"
      @onSave="outletImports" />
    
    <confirmation-modal v-if="showConfirmation"
      :headerText="$t(`tag.messages.confirmation.title`)"
      :message="$t(`tag.messages.confirmation.message`)"
      :showDeleteButton="false"
      :force-tab-index="true"
      @confirmAction="onConfirm"
      @cancelAction="() => { showConfirmation = false; }" />
  </div>
</template>

<script>
import { cloneDeep } from "lodash";
import LoadContent from "../load-content";
import { mapGetters, mapActions, mapState } from "vuex";
import FormRenderer from "../form-renderer";
import InputSelect from "../form-components/input-select";
import FormModal from '../form-modal';
import { CATEGORY_TYPES } from "../../config/const";
import { download_blob } from "../../helpers/download_file";
import ConfirmationModal from '@/components/confirmation-modal';

export default {
  name: "FormTag",

  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    startCategoryId: {
      type: Number,
      required: false,
      default: undefined,
    },
    projectId: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    startCategoryTypeId: {
      type: Number,
      required: false,
      default: undefined,
    },
    tagToEdit: {
      type: [Object, null],
      required: false,
      default: null,
    },
    errors: Array,
  },

  components: {
    InputSelect,
    FormRenderer,
    LoadContent,
    FormModal,
    ConfirmationModal
  },

  data: function () {
    return {
      keyForm: 1,
      tagOutlets: [],
      outletsOptions: [],
      CATEGORY_TYPES,
      categorySelectedId: this.startCategoryId,
      loadingOutlets: false,
      isReady: false,
      showImportOutletsModal: false,
      missingOutletIds: [],
      model: { id: null, name: '',  weight: null, customColor: '', allowRelationship: false, outlets: []},
      showConfirmation: false
    };
  },

  computed: {
    ...mapState({
      user: state => state.user.user.data || {},
      importedOutlets: state => state.projectSetup?.projectSetup?.importedOutlets?.data?.outlets || [],
      missingIds: state => state.projectSetup?.projectSetup?.importedOutlets?.data?.missingIds || [],
      isImportOutletsBusy: state => state.projectSetup?.projectSetup?.isImportOutletsBusy
    }),
    ...mapGetters({
      affiliations: "projectSetup/AFFILIATIONS",
      outletsOptionsToSelect: "admin/outlets/optionsToSelect",
      categories: "projectSetup/CATEGORIES_CAN_ADD_TAGS",
    }),
    submitButtonLabel() {
      if (this.tagToEdit) return "form.button.save";

      return "form.button.add";
    },
    outletsIds() {
      if (this.tagOutlets && this.tagOutlets.length) {
        return this.tagOutlets.map((outlet) => {
          return outlet.id;
        });
      }

      return [];
    },
    modelTag() {
      if (this.tagToEdit && this.tagToEdit.id) {
        const outlets = [...this.model.outlets, ...this.outletsIds]
        return {
          id: this.tagToEdit.id,
          name: this.tagToEdit.name,
          cat_id: this.tagToEdit.cat_id,
          weight: this.tagToEdit.weight,
          customColor: this.tagToEdit.custom_color,
          allowRelationship: this.tagToEdit.allow_relationship,
          outlets: outlets,
        };
      } else if(this.model?.outlets?.length) {
        return this.model
      }

      return undefined;
    },
    disabledSeletCategory() {
      if (this.tagToEdit) return true;

      return false;
    },
    categoriesToList() {
      if (!this.startCategoryTypeId) return this.categories;

      return this.categories.filter((category) => {
        return category.category_type_id === this.startCategoryTypeId;
      });
    },
    categorySelected() {
      if (!this.categories || !this.categories.length) return [];

      return this.categories.find((category) => {
        return category.id === this.categorySelectedId;
      });
    },
    categoryTypeId() {
      if (!this.categorySelected) return undefined;

      return this.categorySelected.category_type_id;
    },
    isMediaTag() {
      return this.categoryTypeId === CATEGORY_TYPES.MEDIA_TAG;
    },
    resource() {
      if (!this.categorySelected || !this.categorySelected.name) return "";

      return this.$i18n.t(
        `tag.category_type_${this.categorySelected.category_type_id}`
      );
    },
    affiliationOptions() {
      return (
        (this.affiliations &&
          this.affiliations.map((a) => ({ value: a.id, label: a.name }))) ||
        []
      );
    },
    baseSelectOptions() {
      if (!this.tagOutlets || !this.tagOutlets.length) return [];

      return this.tagOutlets;
    },
    outletsFieldsAdditions() {
      return {
        affiliation_id: {
          options: this.affiliationOptions,
        },
        outlets_multiple: {
          name: "outlets",
          style: "flex-base: 100%;",
          customClass: "",
          options: this.outletsOptions,
          loading: this.loadingOutlets || this.isImportOutletsBusy,
          multiple: true,
          onSearchChange: (name) => {
            this.callSearchOutlet(name);
          },
          onChange: () => {
            this.refreshKey();
          },
        },
      };
    },
    formFields() {
      switch (this.categoryTypeId) {
        case CATEGORY_TYPES.SOURCE:
          return [
            "first_name",
            "middle_name",
            "last_name",
            "title",
            "job_title",
            "display_name",
            "affiliation_id",
          ];

        case CATEGORY_TYPES.MEDIA_TAG:
          return [
            "name",
            "weight",
            "customColor",
            "allowRelationship",
            "outlets_multiple",
          ];

        case CATEGORY_TYPES.GROUPING:
          return ["name"];

        default:
          return ["name", "weight", "customColor", "allowRelationship"];
      }
    }
  },

  watch: {
    importedOutlets: {
      handler(val){
        const outlets = val.map(o=> o.id);
        this.$set(this.model, 'outlets', [...this.model.outlets, ...outlets]);
        this.setOutletsOptions();
        this.keyForm++;
      },
      deep: true,
    },
    missingIds: {
      handler(val){
        this.missingOutletIds = val
      }
    }
  },

  mounted() {
    if (this.startCategoryTypeId) this.startFirstCategory();
    
    this.callSearchOutlet();

    if (this.tagToEdit) {
      this.setTagOutlets();
    } else {
      this.isReady = true;
      this.setOutletsOptions();
    }
  },

  methods: {
    ...mapActions({
      getOutlets: "admin/outlets/getOutlets",
      getTagOutlets: "tag/getTagOutlets",
      importOutlets: "projectSetup/importOutlets"
    }),
    refreshKey() {
      this.keyForm = this.keyForm++;
    },
    setTagOutlets() {
      this.getTagOutlets({
        projectId: this.projectId,
        tagId: this.tagToEdit.id,
      }).then((outlets) => {
        this.tagOutlets = outlets;
        this.setOutletsOptions();
      })
      .finally(() => {
        this.isReady = true;
      });
    },
    setOutletsOptions() {
      let baseOptions = cloneDeep(this.baseSelectOptions);

      let importedOutletOptions = []
      if(this.importedOutlets && this.importedOutlets.length){
        importedOutletOptions = this.importedOutlets.filter((item) => {
          return this.outletsIds.indexOf(item.id) === -1;
        });
      }

      if (
        baseOptions &&
        baseOptions.length
      ) {
        let outletsOptionsToSelectFiltered = [];

        if (this.outletsOptionsToSelect && this.outletsOptionsToSelect.length) {
          outletsOptionsToSelectFiltered = this.outletsOptionsToSelect.filter((item) => {
            return this.outletsIds.indexOf(item.id) === -1;
          });
        }

        this.outletsOptions = cloneDeep([...baseOptions, ...outletsOptionsToSelectFiltered, ...importedOutletOptions ]);
      } else {
        this.outletsOptions =  cloneDeep([...this.outletsOptionsToSelect, ...importedOutletOptions ]);
      }

      this.outletsOptions.map((item) => {
        item.name = `${item.name} - ID: ${item.id}`;

        return item;
      });
    },
    startFirstCategory() {
      this.categorySelectedId = this.categoriesToList[0] && this.categoriesToList[0].id;
    },
    onConfirm(){
      this.$emit("addTag", this.model);
      this.showConfirmation = false;
    },
    save(data) {
      data.categoryId = this.categorySelected.id;
      data.categoryTypeId = this.categoryTypeId;
      this.model = {...data}
      this.showConfirmation = true;
    },
    callSearchOutlet(name = "") {
      this.loadingOutlets = true;
      this.getOutlets({
        projectId: this.projectId,
        filterValues: {
          name: name,
        },
        pagination: {
          per_page: 20,
          page: 1,
          order: "name",
          sort: "asc",
        },
        forceKeep: true
      })
      .then(() => {
        this.setOutletsOptions();
      }).finally(()=>{
        this.loadingOutlets = false;
      });
    },
    toggleImportOutletsModal(toggle) {
      return this.showImportOutletsModal = toggle;
    },
    outletImports (data) {
      const outlets = new FormData;
      if (data.file_outlets_import && data.file_outlets_import.has('file')){
        outlets.append('file', data.file_outlets_import.get('file'));
      }
      this.importOutlets({ projectUid: this.projectId, payload: outlets });
      this.toggleImportOutletsModal(false);
    },
   
    outletExports() {
      if(this.tagToEdit.id){
        const fileName = `Outlets_${Date.now()}`;
        const data = {
          tag_id: this.tagToEdit.id,
          columns: ['id', 'name', 'media subtype']
        };

        download_blob(`analysis/${this.projectId}/tags/export_outlets`, data, this.user.accessToken, fileName)
        .catch(error => {
          console.error(error);
          this.exporting = false;
          this.$notify({
            group: "store_notifications",
            type: "error",
            text: this.$t("messages.request.error")
          });
        });
      }
    },

    updateModel(newValue, field) {
      if(!field) return;
      this.model[field.name] = newValue[field.name];
    }
  },
};
</script>

<style lang="scss">
@import "../../styles/components/coding-admin/form_create_tag";
</style>